// Text catalog for the Shutdown planning & management service page.
// Icons and images live in src/pages/ShutdownPage.tsx and are aligned by
// index with the card arrays here.

import { type Locale } from "../config";
import { type PageMeta } from "../head";

type ValueProp = { title: string; text: string };
type Phase = { label: string; title: string; items: string[] };
type Faq = { q: string; a: string };

type ShutdownStrings = {
  meta: PageMeta;
  keywords: string;
  ogTitle: string;
  ogDescription: string;
  twTitle: string;
  twDescription: string;
  hero: { eyebrow: string; title: string; body: string; ctaPrimary: string; ctaSecondary: string };
  intro: { eyebrow: string; heading: string; p1: string; p2: string };
  why: { eyebrow: string; heading: string; intro: string; props: ValueProp[] };
  approach: { eyebrow: string; heading: string; intro: string; phases: Phase[] };
  sectors: { eyebrow: string; heading: string; intro: string; items: string[] };
  faq: { eyebrow: string; heading: string; items: Faq[] };
  cta: { heading: string; body: string; button: string };
  jsonLd: { serviceType: string; name: string; description: string; audienceType: string };
};

const ogImage = "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK3989_1350x900.jpg";

export const shutdownStrings: Record<Locale, ShutdownStrings> = {
  da: {
    meta: {
      title: "Planlægning og ledelse af shutdowns | MI Support",
      description:
        "MI Support planlægger og leder shutdowns på industri- og produktionsanlæg. Minimer nedetid, sikr kvalitet og hold budgettet med erfarne shutdown managers.",
      ogImage,
    },
    keywords:
      "shutdown, planlægning shutdown, shutdown manager, shutdown management, nedlukning produktion, industri shutdown",
    ogTitle: "Planlægning og ledelse af shutdowns | MI Support",
    ogDescription:
      "Erfarne shutdown managers planlægger og leder shutdowns, minimal nedetid, fuld kontrol over scope, tid, økonomi og sikkerhed.",
    twTitle: "Planlægning og ledelse af shutdowns | MI Support",
    twDescription: "Shutdown management til industri og life science.",
    hero: {
      eyebrow: "Vores ydelser",
      title: "Planlægning og ledelse af shutdowns",
      body: "MI Supports erfarne shutdown managers planlægger og leder næste shutdown i jeres projekt. Vi sikrer minimal nedetid, fuld kontrol over scope, tid, økonomi og sikkerhed, så anlægget kommer hurtigt og sikkert tilbage i drift.",
      ctaPrimary: "Tag en uforpligtende snak",
      ctaSecondary: "Se alle ydelser",
    },
    intro: {
      eyebrow: "Shutdown management",
      heading: "Hver time tæller, MI Support sikrer kontrol",
      p1: "Under et shutdown er der hundredvis af aktiviteter, mange entreprenører og store mængder reservedele, der skal koordineres på få døgn, samtidig med at sikkerhed, kvalitet og dokumentation skal være i top. Det kræver dedikerede medarbejdere, der forstår både produktion, vedligeholdelse og projektledelse.",
      p2: "MI Supports konsulenter indgår i jeres projektorganisation som en forlænget arm af driften. Vi arbejder tæt sammen med produktionen, indkøb og HSE og bruger jeres egne værktøjer og systemer, fx Primavera P6 eller MS Project.",
    },
    why: {
      eyebrow: "Hvorfor MI Support",
      heading: "Erfarne shutdown managers til komplekse nedlukninger",
      intro:
        "Med MI Supports konsulenter får I erfaring, struktur og et hold, der ved, hvad der skal til for at holde scope, tid og økonomi under kontrol. Vores shutdown managers arbejder som en integreret del af jeres projektorganisation, så I kan fokusere på at få anlægget sikkert tilbage i drift.",
      props: [
        {
          title: "Minimal nedetid",
          text: "Hver time tæller under et shutdown. Med stram planlægning, parallelle arbejder og tæt fremdriftsstyring sikrer vi, at anlægget hurtigt og sikkert kommer tilbage i drift uden uventede forlængelser.",
        },
        {
          title: "Sikkerhed i højsædet",
          text: "Shutdowns er højrisikoaktiviteter med mange entreprenører på samme tid. Vores shutdown managers og sikkerskoordinatorer står for arbejdstilladelser, LOTO, koordinering og en stærk sikkerhedskultur fra dag ét.",
        },
        {
          title: "Erfaring fra industri & life science",
          text: "Vores folk har planlagt og ledet shutdowns på pharma-, fødevare-, kemi- og energianlæg og har erfaring med kravene til GMP, kvalificering, change control og dokumentation i regulerede miljøer.",
        },
      ],
    },
    approach: {
      eyebrow: "Vores tilgang",
      heading: "Fra scope til sikker opstart, med fuldt overblik",
      intro:
        "Vi arbejder struktureret med shutdowns gennem alle tre faser: forberedelse, planlægning og eksekvering. Det giver driften, ledelsen og styregruppen ét samlet overblik over scope, fremdrift, økonomi og risici fra første afklaring til sikker opstart.",
      phases: [
        {
          label: "Fase 01",
          title: "Forberedelse & scope",
          items: [
            "Scope freeze og worklist-management",
            "Risikovurdering og kritikalitetsanalyse",
            "Estimater for tid, ressourcer og økonomi",
            "Kontraktstrategi og udbud af entrepriser",
          ],
        },
        {
          label: "Fase 02",
          title: "Planlægning & mobilisering",
          items: [
            "Detail- og ressourceplaner i Primavera P6 / MS Project",
            "Logistik, lay-down areas og kran-/lift-planlægning",
            "Arbejdstilladelser, LOTO og HSE-setup",
            "Pre-shutdown arbejde og prefabrikation",
          ],
        },
        {
          label: "Fase 03",
          title: "Eksekvering & opstart",
          items: [
            "24/7 shutdown-ledelse og daglig styring",
            "Fremdriftsopfølgning, forecast og rapportering",
            "Ændringshåndtering og emergent work",
            "Sikker opstart, handover og lessons learned",
          ],
        },
      ],
    },
    sectors: {
      eyebrow: "Brancher",
      heading: "Erfaring med shutdowns på krævende anlæg",
      intro:
        "Vores shutdown managers og planlæggere har stået i spidsen for shutdowns på nogle af Danmarks mest komplekse produktionsanlæg, hvor sikkerhed, kvalitet og dokumentation er lige så vigtigt som tidsplanen.",
      items: [
        "Life science & pharma (GMP)",
        "Fødevare- og ingrediensindustri",
        "Kemi og procesindustri",
        "Energi, kraftvarme og forsyning",
        "Olie, gas og biogas",
        "Tunge produktionsanlæg",
      ],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Ofte stillede spørgsmål om shutdowns",
      items: [
        {
          q: "Hvad er en shutdown?",
          a: "En shutdown er en planlagt nedlukning af et anlæg eller en del af det, hvor vedligeholds- og projektaktiviteter samles i ét vindue. Det giver mulighed for at gennemføre arbejde, der ikke kan udføres under normal drift og kræver stram styring af scope, tid, økonomi og sikkerhed.",
        },
        {
          q: "Hvornår bør vi starte planlægningen af næste shutdown?",
          a: "Større shutdowns bør planlægges 12-24 måneder før eksekvering, så scope kan fryses i god tid, kontrakter kan udbydes, langtidsleverancer kan bestilles og prefabrikation kan gennemføres. Tidlig planlægning er det vigtigste parameter for at minimere både varighed og økonomi.",
        },
        {
          q: "Arbejder I i vores egne systemer?",
          a: "Ja. Vores shutdown managers og planlæggere arbejder typisk i jeres egne vedligeholds- og planlægningssystemer, fx Primavera P6 eller MS Project, og rapporterer i Power BI eller Excel-modeller efter jeres standarder.",
        },
        {
          q: "Kan I tage hånd om sikkerheden og arbejdstilladelserne under shutdown?",
          a: "Ja. Vi kan stille både shutdown manager, planlæggere og HSE-/sikkerhedskoordinatorer (AMK-P/B) til rådighed. Vi sikrer arbejdstilladelser, LOTO, koordinering mellem entreprenører og dokumentation, så I står stærkt både under eksekveringen og ved en eventuel myndighedskontrol.",
        },
      ],
    },
    cta: {
      heading: "Skal vi planlægge og lede jeres næste shutdown?",
      body: "Står I overfor en større shutdown, eller mangler jeres nuværende nedlukning stram styring og overblik? MI Supports shutdown managers er klar til en uforpligtende snak om jeres situation og behov.",
      button: "Kontakt os og hør mere",
    },
    jsonLd: {
      serviceType: "Planlægning og ledelse af shutdowns",
      name: "Shutdown management",
      description:
        "Professionel planlægning og ledelse af shutdowns på industri- og produktionsanlæg, scope, tid, økonomi, ressourcer og sikkerhed.",
      audienceType: "Industri, life science, energi, forsyning",
    },
  },

  en: {
    meta: {
      title: "Planning and management of shutdowns | MI Support",
      description:
        "MI Support plans and manages shutdowns at industrial facilities. Minimise downtime and stay on budget with experienced shutdown managers.",
      ogImage,
    },
    keywords:
      "shutdown, shutdown planning, shutdown manager, shutdown management, production shutdown, industrial shutdown",
    ogTitle: "Planning and management of shutdowns | MI Support",
    ogDescription:
      "Experienced shutdown managers plan and lead shutdowns, minimal downtime, full control over scope, time, economy and safety.",
    twTitle: "Planning and management of shutdowns | MI Support",
    twDescription: "Shutdown management for industry and life science.",
    hero: {
      eyebrow: "Our services",
      title: "Planning and management of shutdowns",
      body: "MI Support's experienced shutdown managers plan and lead the next shutdown in your project. We ensure minimal downtime and full control over scope, time, economy and safety, so the plant gets quickly and safely back into operation.",
      ctaPrimary: "Have a no-obligation chat",
      ctaSecondary: "See all services",
    },
    intro: {
      eyebrow: "Shutdown management",
      heading: "Every hour counts, MI Support ensures control",
      p1: "During a shutdown there are hundreds of activities, many contractors and large quantities of spare parts that must be coordinated within a few days, while safety, quality and documentation have to be top notch. This requires dedicated staff who understand production, maintenance and project management alike.",
      p2: "MI Support's consultants join your project organisation as an extended arm of operations. We work closely with production, procurement and HSE and use your own tools and systems, e.g. Primavera P6 or MS Project.",
    },
    why: {
      eyebrow: "Why MI Support",
      heading: "Experienced shutdown managers for complex shutdowns",
      intro:
        "With MI Support's consultants you get experience, structure and a team that knows what it takes to keep scope, time and economy under control. Our shutdown managers work as an integrated part of your project organisation, so you can focus on getting the plant safely back into operation.",
      props: [
        {
          title: "Minimal downtime",
          text: "Every hour counts during a shutdown. With tight planning, parallel work and close progress control we ensure that the plant quickly and safely returns to operation without unexpected extensions.",
        },
        {
          title: "Safety first",
          text: "Shutdowns are high-risk activities with many contractors at the same time. Our shutdown managers and safety coordinators handle work permits, LOTO, coordination and a strong safety culture from day one.",
        },
        {
          title: "Experience from industry & life science",
          text: "Our people have planned and led shutdowns at pharma, food, chemical and energy facilities and have experience with the requirements for GMP, qualification, change control and documentation in regulated environments.",
        },
      ],
    },
    approach: {
      eyebrow: "Our approach",
      heading: "From scope to safe start-up, with full overview",
      intro:
        "We work systematically with shutdowns across all three phases: preparation, planning and execution. This gives operations, management and the steering committee one combined overview of scope, progress, economy and risks from the first clarification to safe start-up.",
      phases: [
        {
          label: "Phase 01",
          title: "Preparation & scope",
          items: [
            "Scope freeze and worklist management",
            "Risk assessment and criticality analysis",
            "Estimates for time, resources and economy",
            "Contract strategy and tendering of works",
          ],
        },
        {
          label: "Phase 02",
          title: "Planning & mobilisation",
          items: [
            "Detailed and resource plans in Primavera P6 / MS Project",
            "Logistics, lay-down areas and crane/lift planning",
            "Work permits, LOTO and HSE setup",
            "Pre-shutdown work and prefabrication",
          ],
        },
        {
          label: "Phase 03",
          title: "Execution & start-up",
          items: [
            "24/7 shutdown management and daily control",
            "Progress follow-up, forecast and reporting",
            "Change management and emergent work",
            "Safe start-up, handover and lessons learned",
          ],
        },
      ],
    },
    sectors: {
      eyebrow: "Sectors",
      heading: "Experience with shutdowns at demanding facilities",
      intro:
        "Our shutdown managers and planners have been at the helm of shutdowns at some of Denmark's most complex production facilities, where safety, quality and documentation are just as important as the schedule.",
      items: [
        "Life science & pharma (GMP)",
        "Food and ingredients industry",
        "Chemical and process industry",
        "Energy, cogeneration and utilities",
        "Oil, gas and biogas",
        "Heavy production facilities",
      ],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Frequently asked questions about shutdowns",
      items: [
        {
          q: "What is a shutdown?",
          a: "A shutdown is a planned closing down of a plant or part of it, where maintenance and project activities are gathered into one window. It makes it possible to carry out work that cannot be done during normal operation and requires tight management of scope, time, economy and safety.",
        },
        {
          q: "When should we start planning the next shutdown?",
          a: "Larger shutdowns should be planned 12-24 months before execution, so that scope can be frozen in good time, contracts can be tendered, long-lead deliveries can be ordered and prefabrication can be carried out. Early planning is the most important parameter for minimising both duration and economy.",
        },
        {
          q: "Do you work in our own systems?",
          a: "Yes. Our shutdown managers and planners typically work in your own maintenance and planning systems, e.g. Primavera P6 or MS Project, and report in Power BI or Excel models according to your standards.",
        },
        {
          q: "Can you take care of safety and the work permits during the shutdown?",
          a: "Yes. We can provide a shutdown manager, planners and HSE/safety coordinators (AMK-P/B). We ensure work permits, LOTO, coordination between contractors and documentation, so you are well positioned both during execution and in the event of an authority inspection.",
        },
      ],
    },
    cta: {
      heading: "Should we plan and manage your next shutdown?",
      body: "Are you facing a larger shutdown, or does your current shutdown lack tight management and overview? MI Support's shutdown managers are ready for a no-obligation chat about your situation and needs.",
      button: "Contact us to learn more",
    },
    jsonLd: {
      serviceType: "Planning and management of shutdowns",
      name: "Shutdown management",
      description:
        "Professional planning and management of shutdowns at industrial and production facilities, scope, time, economy, resources and safety.",
      audienceType: "Industry, life science, energy, utilities",
    },
  },

  de: {
    meta: {
      title: "Planung und Leitung von Shutdowns | MI Support",
      description:
        "MI Support plant und leitet Shutdowns an Industrieanlagen. Minimieren Sie Ausfallzeiten und halten Sie das Budget mit erfahrenen Shutdown Managern.",
      ogImage,
    },
    keywords:
      "Shutdown, Shutdown-Planung, Shutdown Manager, Shutdown Management, Stillstand Produktion, Industrie-Shutdown",
    ogTitle: "Planung und Leitung von Shutdowns | MI Support",
    ogDescription:
      "Erfahrene Shutdown Manager planen und leiten Shutdowns, minimale Ausfallzeit, volle Kontrolle über Scope, Zeit, Wirtschaftlichkeit und Sicherheit.",
    twTitle: "Planung und Leitung von Shutdowns | MI Support",
    twDescription: "Shutdown Management für Industrie und Life Science.",
    hero: {
      eyebrow: "Unsere Leistungen",
      title: "Planung und Leitung von Shutdowns",
      body: "Die erfahrenen Shutdown Manager von MI Support planen und leiten den nächsten Shutdown in Ihrem Projekt. Wir sorgen für minimale Ausfallzeit und volle Kontrolle über Scope, Zeit, Wirtschaftlichkeit und Sicherheit, sodass die Anlage schnell und sicher wieder in Betrieb geht.",
      ctaPrimary: "Unverbindliches Gespräch führen",
      ctaSecondary: "Alle Leistungen ansehen",
    },
    intro: {
      eyebrow: "Shutdown Management",
      heading: "Jede Stunde zählt, MI Support sichert die Kontrolle",
      p1: "Während eines Shutdowns gibt es Hunderte von Aktivitäten, viele Auftragnehmer und große Mengen an Ersatzteilen, die innerhalb weniger Tage koordiniert werden müssen, während Sicherheit, Qualität und Dokumentation auf höchstem Niveau sein müssen. Das erfordert engagierte Mitarbeiter, die sowohl Produktion als auch Instandhaltung und Projektmanagement verstehen.",
      p2: "Die Berater von MI Support sind als verlängerter Arm des Betriebs Teil Ihrer Projektorganisation. Wir arbeiten eng mit Produktion, Einkauf und HSE zusammen und nutzen Ihre eigenen Werkzeuge und Systeme, z. B. Primavera P6 oder MS Project.",
    },
    why: {
      eyebrow: "Warum MI Support",
      heading: "Erfahrene Shutdown Manager für komplexe Stillstände",
      intro:
        "Mit den Beratern von MI Support erhalten Sie Erfahrung, Struktur und ein Team, das weiß, was nötig ist, um Scope, Zeit und Wirtschaftlichkeit unter Kontrolle zu halten. Unsere Shutdown Manager arbeiten als integrierter Teil Ihrer Projektorganisation, sodass Sie sich darauf konzentrieren können, die Anlage sicher wieder in Betrieb zu nehmen.",
      props: [
        {
          title: "Minimale Ausfallzeit",
          text: "Jede Stunde zählt während eines Shutdowns. Mit straffer Planung, parallelen Arbeiten und enger Fortschrittssteuerung sorgen wir dafür, dass die Anlage schnell und sicher wieder in Betrieb geht, ohne unerwartete Verlängerungen.",
        },
        {
          title: "Sicherheit an erster Stelle",
          text: "Shutdowns sind Hochrisikoaktivitäten mit vielen Auftragnehmern zur gleichen Zeit. Unsere Shutdown Manager und Sicherheitskoordinatoren kümmern sich um Arbeitserlaubnisse, LOTO, Koordination und eine starke Sicherheitskultur vom ersten Tag an.",
        },
        {
          title: "Erfahrung aus Industrie & Life Science",
          text: "Unsere Leute haben Shutdowns an Pharma-, Lebensmittel-, Chemie- und Energieanlagen geplant und geleitet und verfügen über Erfahrung mit den Anforderungen an GMP, Qualifizierung, Change Control und Dokumentation in regulierten Umgebungen.",
        },
      ],
    },
    approach: {
      eyebrow: "Unser Ansatz",
      heading: "Vom Scope bis zum sicheren Anfahren, mit vollem Überblick",
      intro:
        "Wir arbeiten strukturiert an Shutdowns über alle drei Phasen hinweg: Vorbereitung, Planung und Ausführung. Das gibt Betrieb, Management und Lenkungsausschuss einen gemeinsamen Überblick über Scope, Fortschritt, Wirtschaftlichkeit und Risiken von der ersten Klärung bis zum sicheren Anfahren.",
      phases: [
        {
          label: "Phase 01",
          title: "Vorbereitung & Scope",
          items: [
            "Scope Freeze und Worklist-Management",
            "Risikobewertung und Kritikalitätsanalyse",
            "Schätzungen für Zeit, Ressourcen und Wirtschaftlichkeit",
            "Vertragsstrategie und Ausschreibung der Gewerke",
          ],
        },
        {
          label: "Phase 02",
          title: "Planung & Mobilisierung",
          items: [
            "Detail- und Ressourcenpläne in Primavera P6 / MS Project",
            "Logistik, Lay-down Areas und Kran-/Hebeplanung",
            "Arbeitserlaubnisse, LOTO und HSE-Setup",
            "Pre-Shutdown-Arbeiten und Vorfertigung",
          ],
        },
        {
          label: "Phase 03",
          title: "Ausführung & Anfahren",
          items: [
            "24/7-Shutdown-Leitung und tägliche Steuerung",
            "Fortschrittsverfolgung, Forecast und Berichtswesen",
            "Änderungsmanagement und Emergent Work",
            "Sicheres Anfahren, Handover und Lessons Learned",
          ],
        },
      ],
    },
    sectors: {
      eyebrow: "Branchen",
      heading: "Erfahrung mit Shutdowns an anspruchsvollen Anlagen",
      intro:
        "Unsere Shutdown Manager und Planer haben Shutdowns an einigen der komplexesten Produktionsanlagen Dänemarks geleitet, wo Sicherheit, Qualität und Dokumentation ebenso wichtig sind wie der Zeitplan.",
      items: [
        "Life Science & Pharma (GMP)",
        "Lebensmittel- und Zutatenindustrie",
        "Chemie und Prozessindustrie",
        "Energie, Kraft-Wärme-Kopplung und Versorgung",
        "Öl, Gas und Biogas",
        "Schwere Produktionsanlagen",
      ],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Häufig gestellte Fragen zu Shutdowns",
      items: [
        {
          q: "Was ist ein Shutdown?",
          a: "Ein Shutdown ist ein geplanter Stillstand einer Anlage oder eines Teils davon, bei dem Instandhaltungs- und Projektaktivitäten in einem Fenster gebündelt werden. Das ermöglicht die Durchführung von Arbeiten, die im Normalbetrieb nicht ausgeführt werden können, und erfordert eine straffe Steuerung von Scope, Zeit, Wirtschaftlichkeit und Sicherheit.",
        },
        {
          q: "Wann sollten wir mit der Planung des nächsten Shutdowns beginnen?",
          a: "Größere Shutdowns sollten 12-24 Monate vor der Ausführung geplant werden, damit der Scope rechtzeitig eingefroren, Verträge ausgeschrieben, Langläuferlieferungen bestellt und die Vorfertigung durchgeführt werden können. Frühe Planung ist der wichtigste Parameter, um sowohl Dauer als auch Wirtschaftlichkeit zu minimieren.",
        },
        {
          q: "Arbeiten Sie in unseren eigenen Systemen?",
          a: "Ja. Unsere Shutdown Manager und Planer arbeiten in der Regel in Ihren eigenen Instandhaltungs- und Planungssystemen, z. B. Primavera P6 oder MS Project, und berichten in Power BI oder Excel-Modellen nach Ihren Standards.",
        },
        {
          q: "Können Sie sich um die Sicherheit und die Arbeitserlaubnisse während des Shutdowns kümmern?",
          a: "Ja. Wir können sowohl Shutdown Manager, Planer als auch HSE-/Sicherheitskoordinatoren (AMK-P/B) bereitstellen. Wir sichern Arbeitserlaubnisse, LOTO, Koordination zwischen den Auftragnehmern und Dokumentation, sodass Sie sowohl während der Ausführung als auch bei einer eventuellen Behördenkontrolle gut aufgestellt sind.",
        },
      ],
    },
    cta: {
      heading: "Sollen wir Ihren nächsten Shutdown planen und leiten?",
      body: "Steht Ihnen ein größerer Shutdown bevor, oder fehlt Ihrem aktuellen Stillstand eine straffe Steuerung und der Überblick? Die Shutdown Manager von MI Support sind bereit für ein unverbindliches Gespräch über Ihre Situation und Ihre Bedürfnisse.",
      button: "Kontaktieren Sie uns für mehr Informationen",
    },
    jsonLd: {
      serviceType: "Planung und Leitung von Shutdowns",
      name: "Shutdown Management",
      description:
        "Professionelle Planung und Leitung von Shutdowns an Industrie- und Produktionsanlagen, Scope, Zeit, Wirtschaftlichkeit, Ressourcen und Sicherheit.",
      audienceType: "Industrie, Life Science, Energie, Versorgung",
    },
  },
};
